/**
 * This file started off from the @react-aria's `useComboBoxState`
 * implementation which can be found here
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/combobox/src/useComboBoxState.ts
 *
 * At the time of writing, @react-aria did not directly support select or
 * combobox with multiple selection. The reason being that there is no such
 * ARIA pattern and some interactions of a multiselect can't be translated to a
 * native <select> which is what `useSelect` tries to emulate.
 *
 * Some relevant discussions can be found here:
 * https://github.com/adobe/react-spectrum/issues/2140
 * https://github.com/adobe/react-spectrum/discussions/2218
 */

import {
  Collection,
  CollectionStateBase,
  FocusStrategy,
  SingleSelection,
  MultipleSelection,
  Node,
  Selection,
} from '@react-types/shared';
import { ComboBoxProps, MenuTriggerAction } from '@react-types/combobox';
import { ListCollection } from '@react-stately/list';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useControlledState } from '@react-stately/utils';
import { useMenuTriggerState } from '@react-stately/menu';
import { MultiSelectState } from './useMultiSelectState';
import { useMultiSelectListState } from './useMultiSelectListState';
import { isSameSelection } from '../../utils/selection';
import { groupSelectedItems } from '../../utils/groupSelectedItems';
import { filterNodes } from './filterNodes';
import { SearchableMultiSelectProps } from './SearchableMultiSelect';

export type MultiComboBoxState<T> = MultiSelectState<T> & {
  /** The current value of the combo box input. */
  inputValue: string;

  /** Sets the value of the combo box input. */
  setInputValue(value: string): void;

  /** Selects the currently focused item and updates the input value. */
  commit(): void;

  /** Opens the menu. */
  open(focusStrategy?: FocusStrategy | null, trigger?: MenuTriggerAction): void;

  /** Toggles the menu. */
  toggle(
    focusStrategy?: FocusStrategy | null,
    trigger?: MenuTriggerAction
  ): void;
};

type FilterFn<T> = (node: Node<T>, inputValue: string) => boolean;

export type MultiComboBoxStateOptions<T> = Omit<
  ComboBoxProps<T>,
  // Omit all single-selection related props from SelectProps and add all
  // multi-selection related props.
  | keyof SingleSelection
  | 'children'
  // custom value doesn't make sense in a multiselect
  | 'allowsCustomValue'
> &
  MultipleSelection &
  CollectionStateBase<T> & {
    /** The filter function used to determine if a option should be included in
     * the combo box list. */
    filter: FilterFn<T>;

    /** Whether the combo box allows the menu to be open when the collection is
     * empty. */
    allowsEmptyCollection?: boolean;

    /** Whether the combo box menu should close on blur. */
    shouldCloseOnBlur?: boolean;

    noInputResetOnSelect?: boolean;

    /** Render the input value based on the selection */
    renderSelectionText: (
      selection: Selection,
      selectedItems: Node<T>[]
    ) => string;

    /**
     * Whether selected items should be grouped in a section at the top.
     * @default true
     */
    groupSelectedItemsOnTop?: boolean;
  };

/** Provides state management for a multi combo box component. Handles building
 * a collection of items from props and manages the option selection state of
 * the combo box. In addition, it tracks the input value, focus state, and
 * other properties of the combo box.
 */
export function useMultiComboBoxState<T extends object>({
  renderSelectionText,
  groupSelectedItemsOnTop = true,
  noInputResetOnSelect = false,
  ...props
}: MultiComboBoxStateOptions<T> &
  Pick<SearchableMultiSelectProps<T>, 'isOpen'>): MultiComboBoxState<T> {
  const {
    filter,
    menuTrigger = 'input',
    allowsEmptyCollection = false,
    shouldCloseOnBlur = true,
  } = props;

  const [showAllItems, setShowAllItems] = useState(false);
  const [isFocused, setFocusedState] = useState(false);
  const [inputValue, setInputValue] = useControlledState<string>(
    props.inputValue,
    props.defaultInputValue ?? '',
    props.onInputChange
  );

  const { selectedItems, ...listState } = useMultiSelectListState({
    ...props,
    onSelectionChange: (keys) => {
      if (props.onSelectionChange) {
        props.onSelectionChange(keys);
      }
    },
    items: props.items ?? props.defaultItems,
  });

  const { collection, selectionManager, disabledKeys } = groupSelectedItemsOnTop
    ? groupSelectedItems(listState)
    : listState;

  // Preserve original collection so we can show all items on demand
  const originalCollection = collection;
  const filteredCollection = useMemo(
    () => filterCollection(collection, inputValue, filter),
    [collection, inputValue, filter]
  );
  const [lastCollection, setLastCollection] = useState(filteredCollection);

  // Track what action is attempting to open the menu
  const menuOpenTrigger = useRef('focus' as MenuTriggerAction);
  const onOpenChange = (open: boolean) => {
    if (props.onOpenChange) {
      props.onOpenChange(open, open ? menuOpenTrigger.current : undefined);
    }

    selectionManager.setFocused(open);
    if (!open) {
      selectionManager.setFocusedKey(null);
    }
  };

  const triggerState = useMenuTriggerState({
    ...props,
    onOpenChange,
    isOpen: undefined,
    defaultOpen: undefined,
  });
  const isOpen = props.isOpen ?? triggerState.isOpen;

  const open = (focusStrategy?: FocusStrategy, trigger?: MenuTriggerAction) => {
    const displayAllItems =
      trigger === 'manual' || (trigger === 'focus' && menuTrigger === 'focus');
    // Prevent open operations from triggering if there is nothing to display
    // Also prevent open operations from triggering if items are uncontrolled
    // but defaultItems is empty, even if displayAllItems is true. This is to
    // prevent comboboxes with empty defaultItems from opening but allow
    // controlled items comboboxes to open even if the inital list is empty
    // (assumption is user will provide swap the empty list with a base list
    // via onOpenChange returning `menuTrigger` manual)
    if (
      allowsEmptyCollection ||
      filteredCollection.size > 0 ||
      (displayAllItems && originalCollection.size > 0) ||
      props.items
    ) {
      if (displayAllItems && !isOpen && props.items === undefined) {
        // Show all items if menu is manually opened. Only care about this if
        // items are undefined
        setShowAllItems(true);
      }

      menuOpenTrigger.current = trigger || 'manual';
      triggerState.open(focusStrategy);
    }
  };

  const toggle = (
    focusStrategy?: FocusStrategy,
    trigger?: MenuTriggerAction
  ) => {
    const displayAllItems =
      trigger === 'manual' || (trigger === 'focus' && menuTrigger === 'focus');
    // If the menu is closed and there is nothing to display, early return so
    // toggle isn't called to prevent extraneous onOpenChange
    if (
      !(
        allowsEmptyCollection ||
        filteredCollection.size > 0 ||
        (displayAllItems && originalCollection.size > 0) ||
        props.items
      ) &&
      !isOpen
    ) {
      return;
    }

    if (displayAllItems && !isOpen && props.items === undefined) {
      // Show all items if menu is toggled open. Only care about this if items
      // are undefined
      setShowAllItems(true);
    }

    // Only update the menuOpenTrigger if menu is currently closed
    if (!isOpen) {
      menuOpenTrigger.current = trigger || 'manual';
    }

    toggleMenu(focusStrategy);
  };

  // If menu is going to close, save the current collection so we can freeze
  // the displayed collection when the user clicks outside the popover to
  // close the menu. Prevents the menu contents from updating as the menu
  // closes.
  const toggleMenu = useCallback(
    (focusStrategy) => {
      if (isOpen) {
        setLastCollection(filteredCollection);
      }

      triggerState.toggle(focusStrategy);
    },
    [triggerState, filteredCollection]
  );

  const closeMenu = useCallback(() => {
    if (isOpen) {
      setLastCollection(filteredCollection);
      triggerState.close();
    }
  }, [triggerState, filteredCollection]);

  const lastValue = useRef(inputValue);
  const resetInputValue = () => {
    const itemText = '';
    lastValue.current = itemText;
    setInputValue(itemText);
  };

  const isInitialRender = useRef(true);
  const lastSelectedKeys = useRef(
    props.selectedKeys ?? props.defaultSelectedKeys ?? null
  );
  const lastSelectedItemsText = useRef(
    renderSelectionText(selectionManager.selectedKeys, selectedItems)
  );
  // intentional omit dependency array, want this to happen on every render
  useEffect(() => {
    // Open and close menu automatically when the input value changes if the input is focused,
    // and there are items in the collection or allowEmptyCollection is true.
    if (
      isFocused &&
      (filteredCollection.size > 0 || allowsEmptyCollection) &&
      !isOpen &&
      inputValue !== lastValue.current &&
      menuTrigger !== 'manual'
    ) {
      open(undefined, 'input');
    }
    // Close the menu if the collection is empty. Don't close menu if filtered
    // collection size is 0 but we are currently showing all items via button
    // press
    if (
      !showAllItems &&
      !allowsEmptyCollection &&
      isOpen &&
      filteredCollection.size === 0
    ) {
      closeMenu();
    }

    // Clear focused key when input value changes and display filtered
    // collection again.
    if (inputValue !== lastValue.current) {
      selectionManager.setFocusedKey(null);
      setShowAllItems(false);
      lastValue.current = inputValue;
    }

    // If the selectedKey changed, update the input value.
    if (
      !noInputResetOnSelect &&
      !isSameSelection(selectionManager.selectedKeys, lastSelectedKeys.current)
    ) {
      lastValue.current = inputValue;
      resetInputValue();
    }

    // Update the inputValue if the selected item's text changes from its last
    // tracked value. This is to handle cases where a selectedKey is specified
    // but the items aren't available (async loading) or the selected item's
    // text value updates. Only reset if the user isn't currently within the
    // field so we don't erroneously modify user input. If inputValue is
    // controlled, it is the user's responsibility to update the inputValue
    // when items change.
    const selectedItemsText = renderSelectionText(
      selectionManager.selectedKeys,
      selectedItems
    );
    if (
      !isFocused &&
      selectionManager.selectedKeys != null &&
      props.inputValue === undefined &&
      isSameSelection(selectionManager.selectedKeys, lastSelectedKeys.current)
    ) {
      if (lastSelectedItemsText.current !== selectedItemsText) {
        setInputValue(selectedItemsText);
      }
    }

    isInitialRender.current = false;
    lastSelectedKeys.current = selectionManager.selectedKeys;
    lastSelectedItemsText.current = selectedItemsText;
  });

  const commitSelection = () => {
    props.onSelectionChange?.(selectionManager.selectedKeys);
    lastValue.current = renderSelectionText(
      selectionManager.selectedKeys,
      selectedItems
    );
    resetInputValue();
    closeMenu();
  };

  const commit = () => {
    if (isOpen && selectionManager.focusedKey != null) {
      selectionManager.toggleSelection(selectionManager.focusedKey);
    } else {
      // Reset inputValue if no item is focused but user triggers a commit
      commitSelection();
    }
  };

  const close = () => {
    commitSelection();
  };

  const setFocused = (isFocused: boolean) => {
    if (isFocused) {
      if (menuTrigger === 'focus') {
        open(undefined, 'focus');
      }
    } else if (shouldCloseOnBlur) {
      close();
    }

    setFocusedState(isFocused);
  };

  const displayedCollection = useMemo(() => {
    if (isOpen) {
      if (showAllItems) {
        return originalCollection;
      } else {
        return filteredCollection;
      }
    } else {
      return lastCollection;
    }
  }, [
    isOpen,
    originalCollection,
    filteredCollection,
    showAllItems,
    lastCollection,
  ]);

  return {
    ...triggerState,
    isOpen,
    toggle,
    open,
    close,
    selectionManager,
    selectedItems,
    disabledKeys,
    isFocused,
    setFocused,
    collection: displayedCollection,
    inputValue,
    setInputValue,
    commit,
  };
}

function filterCollection<T extends object>(
  collection: Collection<Node<T>>,
  inputValue: string,
  filter: FilterFn<T>
): Collection<Node<T>> {
  return new ListCollection<T>(
    filterNodes(collection, collection, (node) => filter(node, inputValue))
  );
}
